import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "./CartContext";
import api from "./axiosConfig";
import {
  ShoppingCart, User, Home, Percent, Package, LogOut, 
  ChevronDown, Settings, CreditCard, Bell
} from "lucide-react";
import InAppNotifications from "./components/InAppNotifications";
import { initClientSocket } from "./socketClient";

export default function Header() {
  const location = useLocation();
  const { itemCount } = useCart();
  const [firstName, setFirstName] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const phone = localStorage.getItem("customerPhone");
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!phone) return;
    initClientSocket(phone);
    api.get(`/users/client/${phone}`)
      .then((res) => {
        const user = res.data.user;
        if (user?.firstName) setFirstName(user.firstName.split(" ")[0]);
      })
      .catch(() => setFirstName(""));
  }, [phone]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navItems = [
    { path: "/", label: "Accueil", icon: Home },
    { path: "/promotions", label: "Offres", icon: Percent },
    { path: "/commandes", label: "Suivi", icon: Package },
  ];

  const handleLogout = () => {
    localStorage.removeItem("customerPhone");
    window.location.href = "/";
  };

  const getIconLinkClasses = (path) => {
    const isActive = (location.pathname.startsWith(path) && path !== "/") || location.pathname === path;
    return `relative flex items-center justify-center w-11 h-11 rounded-2xl transition-all duration-300 group
    ${isActive ? "text-[#f34141] bg-red-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"}`;
  };

  return (
    <header className="fixed top-0 left-0 z-[100] w-full font-sans bg-white/80 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 mx-auto max-w-7xl">
        
        {/* LOGO */}
        <Link to="/" className="flex items-center transition-transform hover:scale-105 active:scale-95">
          <img src="/fulllogo_transparent_nobuffer.png" alt="Souqi Logo" className="w-auto h-10" />
        </Link>

        {/* NAV CENTRALE - Désormais plus "App like" */}
        <nav className="hidden md:flex items-center bg-slate-50 p-1.5 rounded-[1.5rem] border border-slate-100">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black transition-all ${
              location.pathname === item.path ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}>
              <item.icon size={16} />
              {item.label}
            </Link>
          ))}
        </nav>

        {/* ACTIONS DROITE */}
        <div className="flex items-center gap-3">
          
          {/* COMPTE DROPDOWN */}
          {phone ? (
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-3 pl-2 pr-4 py-2 rounded-2xl transition-all border ${
                  isDropdownOpen ? "bg-white border-slate-200 shadow-lg" : "bg-white border-transparent hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-center w-9 h-9 bg-slate-100 rounded-xl text-slate-600">
                  <User size={20} />
                </div>
                <div className="hidden text-left md:block">
                  <p className="text-[10px] font-black text-slate-300 uppercase leading-none mb-1">Mon Profil</p>
                  <p className="text-sm font-black leading-none text-slate-800">{firstName || "Compte"}</p>
                </div>
                <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 w-64 mt-4 bg-white border border-slate-100 shadow-2xl shadow-slate-200 rounded-[2rem] py-4 overflow-hidden animate-in fade-in zoom-in duration-200 origin-top-right">
                  <div className="px-6 py-3 mb-2">
                    <p className="text-xs font-black tracking-widest uppercase text-slate-300">Bienvenue</p>
                    <p className="text-lg font-black truncate text-slate-900">{firstName || "Cher client"}</p>
                  </div>
                  
                  <div className="px-2 space-y-1">
                    <Link to="/compte" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-4 px-4 py-3 font-bold transition-colors rounded-xl text-slate-600 hover:bg-slate-50">
                      <Settings size={18} className="text-slate-400" /> Paramètres
                    </Link>
                    <Link to="/commandes" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-4 px-4 py-3 font-bold transition-colors rounded-xl text-slate-600 hover:bg-slate-50">
                      <Package size={18} className="text-slate-400" /> Mes Commandes
                    </Link>
                    <button onClick={handleLogout} className="flex items-center w-full gap-4 px-4 py-3 font-bold text-red-500 transition-colors rounded-xl hover:bg-red-50">
                      <LogOut size={18} /> Déconnexion
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login-client" className="flex items-center gap-2 px-6 py-3 text-sm font-black text-white transition-all shadow-lg bg-slate-900 rounded-2xl hover:bg-slate-800 active:scale-95 shadow-slate-200">
              <User size={18} />
              Connexion
            </Link>
          )}

          {/* NOTIFS & PANIER */}
          <div className="flex items-center gap-2">
              {phone && <InAppNotifications phone={phone} />}
              <Link to="/panier" className="relative w-12 h-12 flex items-center justify-center bg-[#f34141] text-white rounded-2xl shadow-lg shadow-red-100 hover:scale-105 transition-all active:scale-90 group">
                <ShoppingCart size={22} />
                {itemCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center text-[10px] font-black bg-slate-900 text-white rounded-full ring-4 ring-white group-hover:animate-bounce">
                    {itemCount}
                  </span>
                )}
              </Link>
          </div>
        </div>
      </div>
    </header>
  );
}